import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AppThemeProvider from './themes/Theme.jsx';
import LoginForm from './components/Login.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import MainLayout from './components/MainLayout.jsx';
import { authClient } from './lib/auth-client';
import { prefetchCoreAppData } from './lib/app-bootstrap.js';
import { OrganizationProvider } from './contexts/OrganizationProvider.jsx';

// Lazy load components
const Home = lazy(() => import('./components/Home.jsx'));
const ResetPasswordForm = lazy(() => import('./components/ResetPasswordForm.jsx'));
const AddStaffMember = lazy(() => import('./components/AddStaffMember.jsx'));
const ProductList = lazy(() => import('./components/products/ProductList.jsx'));
const ProductForm = lazy(() => import('./components/products/ProductForm.jsx'));
const ProductDetail = lazy(() => import('./components/products/ProductDetail.jsx'));
const AcceptInvitation = lazy(() => import('./components/AcceptInvitation.jsx'));
const ServiceList = lazy(() => import('./components/services/ServiceList.jsx'));
const ServiceForm = lazy(() => import('./components/services/ServiceForm.jsx'));
const ServiceDetail = lazy(() => import('./components/services/ServiceDetail.jsx'));
const CustomerList = lazy(() => import('./components/customers/CustomerList.jsx'));
const CustomerForm = lazy(() => import('./components/customers/CustomerForm.jsx'));
const CustomerDetail = lazy(() => import('./components/customers/CustomerDetail.jsx'));
const CustomerIntakeQrPage = lazy(() => import('./components/customers/CustomerIntakeQrPage.jsx'));
const PublicCustomerIntake = lazy(() => import('./components/customers/PublicCustomerIntake.jsx'));
const Management = lazy(() => import('./components/Management.jsx'));
const Gallery = lazy(() => import('./components/gallery/Gallery.jsx'));
const StaffList = lazy(() => import('./components/staff/StaffList.jsx'));
const StaffDetail = lazy(() => import('./components/staff/StaffDetail.jsx'));
const SuppliesList = lazy(() => import('./components/supplies/SuppliesList.jsx'));
const SuppliesForm = lazy(() => import('./components/supplies/SuppliesForm.jsx'));
const SuppliesDetail = lazy(() => import('./components/supplies/SuppliesDetail.jsx'));
const PaymentMethodList = lazy(() => import('./components/payment-methods/PaymentMethodList.jsx'));
const PaymentMethodForm = lazy(() => import('./components/payment-methods/PaymentMethodForm.jsx'));
const SalesList = lazy(() => import('./components/sales/SalesList.jsx'));
const SalesForm = lazy(() => import('./components/sales/SalesForm.jsx'));
const SaleDetail = lazy(() => import('./components/sales/SaleDetail.jsx'));
const PurchasesList = lazy(() => import('./components/purchases/PurchasesList.jsx'));
const PurchasesForm = lazy(() => import('./components/purchases/PurchasesForm.jsx'));
const PurchaseDetail = lazy(() => import('./components/purchases/PurchaseDetail.jsx'));
const Statistics = lazy(() => import('./components/Statistics.jsx'));
const DailySessionsList = lazy(() => import('./components/daily-sessions/DailySessionsList.jsx'));
const DailySessionForm = lazy(() => import('./components/daily-sessions/DailySessionForm.jsx'));
const ReloadPrompt = lazy(() => import('./components/ReloadPrompt.jsx'));
const NotificationPrompt = lazy(() => import('./components/notifications/NotificationPrompt.jsx'));
const NotificationList = lazy(() => import('./components/notifications/NotificationList.jsx'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail.jsx'));
const PricingGrid = lazy(() => import('./components/pricing/PricingGrid.jsx'));
const Settings = lazy(() => import('./components/settings/Settings.jsx'));
const MembershipDashboard = lazy(() => import('./components/memberships/MembershipDashboard.jsx'));
const MembershipPurchase = lazy(() => import('./components/memberships/MembershipPurchase.jsx'));
const MembershipConsume = lazy(() => import('./components/memberships/MembershipConsume.jsx'));
const MembershipPlanForm = lazy(() => import('./components/memberships/MembershipPlanForm.jsx'));
const MyCommissions = lazy(() => import('./components/staff/MyCommissions.jsx'));
const CommissionSettings = lazy(() => import('./components/settings/CommissionSettings.jsx'));
const LandingEntry = lazy(() => import('./components/LandingEntry.jsx'));

// SaaS Components
const SubscriptionDashboard = lazy(() => import('./components/saas/SubscriptionDashboard.jsx'));
const SaasPricing = lazy(() => import('./components/saas/SaasPricing.jsx'));
const OrganizationSettings = lazy(() => import('./components/saas/OrganizationSettings.jsx'));
const LoyaltyDashboard = lazy(() => import('./components/loyalty/LoyaltyDashboard.jsx'));
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard.jsx'));
const PlanRestrictedRoute = lazy(() => import('./components/PlanRestrictedRoute.jsx'));
const FeatureGuard = lazy(() => import('./components/shared/FeatureGuard.jsx'));
const AppointmentsDashboard = lazy(() => import('./components/appointments/AppointmentsDashboard.jsx'));
const PublicBooking = lazy(() => import('./components/appointments/PublicBooking.jsx'));
const LoadingScreen = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100dvh",
      bgcolor: "background.default"
    }}>
    <CircularProgress />
  </Box>
);

import {
  consumePendingNavigation,
  identifyAnalyticsUser,
  resetAnalyticsIdentity,
  trackEvent,
} from './lib/app-telemetry';


const SettlementsList = lazy(() => import('./components/staff/SettlementsList.jsx'));
const StaffSettlementDetail = lazy(() => import('./components/staff/StaffSettlementDetail.jsx'));
const StaffSettlementPreview = lazy(() => import('./components/staff/StaffSettlementPreview.jsx'));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function NavigationAnalytics() {
  const location = useLocation();

  useEffect(() => {
    const pendingNavigation = consumePendingNavigation(location.pathname);

    trackEvent('screen_view', {
      path: location.pathname,
      navigation_source: pendingNavigation?.source || 'direct',
      navigation_trigger: pendingNavigation?.trigger || 'route_change',
      navigation_label: pendingNavigation?.label || null,
      previous_path: pendingNavigation?.from || null,
    });
  }, [location.pathname]);

  return null;
}

function AnalyticsIdentity({ session, isPending }) {
  useEffect(() => {
    if (isPending) return;

    if (!session?.user?.id) {
      resetAnalyticsIdentity();
      return;
    }

    identifyAnalyticsUser(session);
  }, [isPending, session]);

  return null;
}

function BackgroundBootstrap({ session }) {
  const queryClient = useQueryClient();
  const bootstrappedSessionRef = useRef(null);

  useEffect(() => {
    if (!session?.user?.id) {
      bootstrappedSessionRef.current = null;
      return;
    }

    const bootstrapKey = `${session.user.id}:${session.barbershopId ?? 'owner'}`;
    if (bootstrappedSessionRef.current === bootstrapKey) return;

    bootstrappedSessionRef.current = bootstrapKey;
    void prefetchCoreAppData(queryClient, session);
  }, [queryClient, session]);

  return null;
}

function DeferredAppPrompts() {
  const [shouldRenderPrompts, setShouldRenderPrompts] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setShouldRenderPrompts(true);
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, []);

  if (!shouldRenderPrompts) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ReloadPrompt />
      <NotificationPrompt />
    </Suspense>
  );
}


function App() {
  const { data: session, isPending } = authClient.useSession();

  return (
    <AppThemeProvider>
      <OrganizationProvider>
        <ScrollToTop />
        <AnalyticsIdentity session={session} isPending={isPending} />
        <BackgroundBootstrap session={session} />
        <NavigationAnalytics />
        <DeferredAppPrompts />

        <main>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* RUTAS PÚBLICAS */}
              <Route
                path="/"
                element={
                  session?.user
                    ? <Navigate to="/home" replace />
                    : <LoginForm />
                }
              />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/sumate/:token" element={<PublicCustomerIntake />} />
              <Route path="/reservas/:token" element={<PublicBooking />} />
              <Route
                path="/accept-invitation/:token"
                element={<AcceptInvitation />}
              />
              <Route path="/landing-entry" element={<LandingEntry />} />
              <Route
                path="/onboarding"
                element={
                  session?.user && session?.organizationId
                    ? <Navigate to="/home" replace />
                    : <OnboardingWizard />
                }
              />
              <Route
                path="/planes"
                element={
                  session?.user
                    ? <Navigate to="/saas/pricing" replace />
                    : <SaasPricing />
                }
              />

              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/home" element={<Home />} />

                  {/* Rutas Restringidas por Plan */}
                  <Route element={<PlanRestrictedRoute />}>
                    <Route path="/add-staff" element={<AddStaffMember />} />
                    <Route path="/staff" element={<StaffList />} />
                    <Route path="/staff/:id" element={<StaffDetail />} />
                    <Route path="/settlements" element={<SettlementsList />} />
                    <Route path="/settlements/preview/:userId" element={<StaffSettlementPreview />} />
                    <Route path="/settlements/:id" element={<StaffSettlementDetail />} />
                    <Route path="/my-commissions" element={<MyCommissions />} />
                  </Route>

                  <Route path="/products" element={<ProductList />} />
                  <Route path="/products/new" element={<ProductForm />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/products/edit/:id" element={<ProductForm />} />
                  <Route path="/services" element={<ServiceList />} />
                  <Route path="/services/new" element={<ServiceForm />} />
                  <Route path="/services/:id" element={<ServiceDetail />} />
                  <Route path="/services/edit/:id" element={<ServiceForm />} />
                  <Route path="/customers" element={<CustomerList />} />
                  <Route path="/customers/intake-qr" element={<CustomerIntakeQrPage />} />
                  <Route path="/customers/new" element={<CustomerForm />} />
                  <Route path="/customers/:id" element={<CustomerDetail />} />
                  <Route path="/customers/edit/:id" element={<CustomerForm />} />
                  <Route path="/management" element={<Management />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/supplies" element={<SuppliesList />} />
                  <Route path="/supplies/new" element={<SuppliesForm />} />
                  <Route path="/supplies/:id" element={<SuppliesDetail />} />
                  <Route path="/supplies/edit/:id" element={<SuppliesForm />} />
                  <Route path="/payment-methods" element={<PaymentMethodList />} />
                  <Route
                    path="/payment-methods/new"
                    element={<PaymentMethodForm />}
                  />
                  <Route
                    path="/payment-methods/edit/:id"
                    element={<PaymentMethodForm />}
                  />
                  <Route path="/sales" element={<SalesList />} />
                  <Route path="/sales/new" element={<SalesForm />} />
                  <Route path="/sales/:id" element={<SaleDetail />} />
                  <Route path="/purchases" element={<PurchasesList />} />
                  <Route path="/purchases/new" element={<PurchasesForm />} />
                  <Route path="/purchases/:id" element={<PurchaseDetail />} />
                  <Route path="/stats" element={<Statistics />} />
                  <Route
                    path="/notifications"
                    element={<NotificationList />}
                  />
                  <Route
                    path="/settings"
                    element={<Settings />}
                  />
                  <Route path="/daily-closing" element={<DailySessionsList />} />
                  <Route path="/daily-closing/new" element={<DailySessionForm />} />
                  <Route path="/pricing-grid" element={<PricingGrid />} />
                  <Route path="/commissions" element={<CommissionSettings />} />
                  <Route path="/memberships" element={
                    <FeatureGuard feature="memberships" showLockOverlay>
                      <MembershipDashboard />
                    </FeatureGuard>
                  } />
                  <Route path="/memberships/new" element={
                    <FeatureGuard feature="memberships" showLockOverlay>
                      <MembershipPurchase />
                    </FeatureGuard>
                  } />
                  <Route path="/memberships/consume" element={
                    <FeatureGuard feature="memberships" showLockOverlay>
                      <MembershipConsume />
                    </FeatureGuard>
                  } />
                  <Route path="/memberships/plans/new" element={
                    <FeatureGuard feature="memberships" showLockOverlay>
                      <MembershipPlanForm />
                    </FeatureGuard>
                  } />
                  <Route path="/memberships/plans/edit/:id" element={
                    <FeatureGuard feature="memberships" showLockOverlay>
                      <MembershipPlanForm />
                    </FeatureGuard>
                  } />
                  {/* SaaS Routes */}
                  <Route path="/saas/dashboard" element={<SubscriptionDashboard />} />
                  <Route path="/saas/pricing" element={<SaasPricing />} />
                  <Route path="/saas/settings" element={<OrganizationSettings />} />
                  <Route path="/loyalty" element={
                    <FeatureGuard feature="loyalty" showLockOverlay>
                      <LoyaltyDashboard />
                    </FeatureGuard>
                  } />

                  <Route path="/appointments" element={<AppointmentsDashboard />} />
                </Route>
              </Route>

              {/* Redirección por defecto */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </OrganizationProvider>
    </AppThemeProvider>
  );
}

export default App;
